import { useState } from "react"
import { router } from "expo-router"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { View, ScrollView, Pressable, Modal, Platform, ActivityIndicator } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

import { createOrderAdminInput } from "@white-labels/input/src/order"
import { trpc } from "@src/utils/trpc"
import { ar } from "@src/i18n/ar"
import { Header, CustomerPicker, ProductPicker, Button, Text, TextInput, MCIcon, Image } from "@src/components"
import { priceFromDBFormat, sharedStyles, ENV } from "@src/utils"
import { analytics } from "@src/utils/analytics"
import { Alert } from "./ui/confirm"
import { PriceInput } from "./price-input"

type OrderFormProps = {
  orderId?: number
}

export function OrderForm({ orderId }: OrderFormProps) {
  const isEditMode = orderId !== undefined
  const [showAddressModal, setShowAddressModal] = useState(false)

  const createOrder = trpc.orders.create.useMutation()
  const updateOrder = trpc.orders.update.useMutation()
  const utils = trpc.useUtils()

  const order = trpc.orders.get.useQuery({ id: orderId as number }, { enabled: isEditMode })

  const { control, handleSubmit, formState, watch, setValue } = useForm({
    resolver: zodResolver(createOrderAdminInput),
    defaultValues: {
      customerId: order.data?.customerId,
      addressId: order.data?.addressId,
      source: order.data?.source ?? "whatsapp",
      items: order.data?.items?.map((i) => ({ productId: i.productId, quantity: i.quantity })) ?? [],
      discount: order.data?.discount ?? 0,
      deliveryInstructions: order.data?.deliveryInstructions ?? undefined,
    },
  })

  const customerId = watch("customerId")
  const items = watch("items")
  const addressId = watch("addressId")
  const discount = watch("discount")

  const customer = trpc.customers.get.useQuery({ id: customerId }, { enabled: customerId !== undefined })

  const handleProductsChange = (productIds: number[]) => {
    const newItems = productIds.map((id) => {
      const existing = items.find((item) => item.productId === id)
      return existing || { productId: id, quantity: 1 }
    })
    setValue("items", newItems)
  }

  const updateQuantity = (productId: number, quantity: number) => {
    if (quantity === 0) {
      const newItems = items.filter((item) => item.productId !== productId)
      setValue("items", newItems)
      return
    }
    const newItems = items.map((item) => (item.productId === productId ? { ...item, quantity: Math.max(1, quantity) } : item))
    setValue("items", newItems)
  }

  const handleSubmitOrder = handleSubmit(async (data) => {
    if (!data.customerId) {
      Alert.alert(ar.error, ar.pleaseSelectCustomerFirst)
      return
    }
    if (!data.addressId) {
      Alert.alert(ar.error, ar.selectAddress)
      return
    }
    if (data.items.length === 0) {
      Alert.alert(ar.error, ar.selectProducts)
      return
    }

    try {
      if (isEditMode) {
        await updateOrder.mutateAsync({
          id: orderId,
          items: data.items,
          addressId: data.addressId,
          discount: data.discount,
        })
        await utils.orders.get.invalidate({ id: orderId })
        await utils.orders.list.invalidate()
        Alert.alert(ar.success, ar.orderUpdated)
        router.back()
      } else {
        const result = await createOrder.mutateAsync(data)
        await utils.orders.list.invalidate()
        analytics.logEvent({ name: "order_created", params: { items_count: data.items.length } })
        Alert.alert(ar.success, ar.orderCreatedSuccessfully)
        router.replace(`/dashboard/orders/${result.orderId}`)
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : ar.error
      Alert.alert(ar.error, errorMessage)
    }
  })

  const addresses = customer.data?.addresses ?? []
  const { isSubmitting, isValid } = formState
  const loading = createOrder.isPending || updateOrder.isPending || isSubmitting

  return (
    <>
      <ScrollView contentContainerStyle={sharedStyles.contentContainer} className="flex-1">
        <View className="flex-row flex-wrap gap-4">
          {/* Customer Selection - disabled in edit mode */}
          <View className="flex-1 min-w-[250px]">
            <Text className="mb-2 font-semibold text-gray-700">{ar.customers} *</Text>
            <Controller
              control={control}
              name="customerId"
              render={({ field }) => (
                <CustomerPicker
                  value={field.value}
                  onChange={(id) => {
                    if (isEditMode) return // Don't allow changing in edit mode
                    field.onChange(id)
                    // @ts-expect-error
                    setValue("addressId", undefined)
                  }}
                  disabled={isEditMode}
                />
              )}
            />
          </View>

          {/* Address Selection */}
          <View className="flex-1 min-w-[250px]">
            <Text className="mb-2 font-semibold text-gray-700">{ar.addresses} *</Text>
            <Controller
              control={control}
              name="addressId"
              render={({ field }) => (
                <Pressable
                  className={`h-11 bg-gray-50 border border-gray-300 rounded px-4 justify-center ${!customerId ? "opacity-50" : "active:opacity-50"}`}
                  onPress={() => {
                    if (!customerId) {
                      Alert.alert(ar.error, ar.pleaseSelectCustomerFirst)
                      return
                    }
                    setShowAddressModal(true)
                  }}
                  disabled={!customerId}
                >
                  <Text className={field.value ? "text-gray-900" : "text-gray-500"} numberOfLines={1}>
                    {field.value
                      ? (() => {
                          const selectedAddr = addresses.find((a) => a.id === field.value)
                          if (!selectedAddr) return ar.selectAddress
                          return selectedAddr.label || [selectedAddr.building, selectedAddr.floor, selectedAddr.flat].filter(Boolean).join(", ") || ar.selectAddress
                        })()
                      : ar.selectAddress}
                  </Text>
                </Pressable>
              )}
            />
          </View>

          {/* Order Source - hidden in edit mode */}
          {!isEditMode && (
            <View className="min-w-[200px] flex-1">
              <Text className="mb-2 font-semibold text-gray-700">{ar.orderSource} *</Text>
              <Controller
                control={control}
                name="source"
                render={({ field }) => (
                  <View className="flex-row gap-2 h-11">
                    <Pressable
                      onPress={() => field.onChange("phone")}
                      className={`flex-1 px-2 rounded border flex-row items-center justify-center gap-2 ${
                        field.value === "phone" ? "bg-blue-50 border-blue-500" : "bg-white border-gray-300"
                      }`}
                    >
                      <MCIcon name="phone" size={18} color={field.value === "phone" ? "#3b82f6" : "#6b7280"} />
                      <Text className={field.value === "phone" ? "text-blue-600 font-semibold text-xs" : "text-gray-700 text-xs"}>{ar.sourcePhone}</Text>
                    </Pressable>
                    <Pressable
                      onPress={() => field.onChange("whatsapp")}
                      className={`flex-1 px-2 rounded border flex-row items-center justify-center gap-2 ${
                        field.value === "whatsapp" ? "bg-blue-50 border-blue-500" : "bg-white border-gray-300"
                      }`}
                    >
                      <MCIcon name="whatsapp" size={18} color={field.value === "whatsapp" ? "#3b82f6" : "#6b7280"} />
                      <Text className={field.value === "whatsapp" ? "text-blue-600 font-semibold text-xs" : "text-gray-700 text-xs"}>{ar.sourceWhatsapp}</Text>
                    </Pressable>
                  </View>
                )}
              />
            </View>
          )}
        </View>

        <Text className="mb-2 font-semibold text-gray-700">{ar.selectProducts} *</Text>
        <ProductPicker
          value={items.map((item) => item.productId)}
          types={["LOCAL_PRODUCT", "STANDARD_PRODUCT", "WEIGHT_DEFINED_PRODUCT"]}
          onChange={handleProductsChange}
          mode="multiple"
        />

        <OrderItems items={items} updateQuantity={updateQuantity} />
        <OrderSummary items={items} addressId={addressId} discount={discount} customerId={customerId} />

        <Text className="mb-2 font-semibold text-gray-700">{ar.deliveryInstructions}</Text>
        <Controller
          control={control}
          name="deliveryInstructions"
          render={({ field }) => <TextInput placeholder={ar.deliveryInstructions} value={field.value} onChangeText={field.onChange} multiline numberOfLines={3} />}
        />

        {/* Discount */}
        <Text className="mb-2 font-semibold text-gray-700">{ar.discount}</Text>
        <Controller control={control} name="discount" render={({ field }) => <PriceInput value={field.value} onValueChange={field.onChange} className="mb-2" />} />

        <View className="flex-row gap-3 self-end">
          <Button onPress={router.back} disabled={loading} className="px-4 bg-gray-500">
            {ar.cancel}
          </Button>
          <Button onPress={handleSubmitOrder} loading={loading} disabled={loading || !isValid} className="px-4">
            {isEditMode ? ar.saveChanges : ar.createOrder}
          </Button>
        </View>
        <SafeAreaView edges={["bottom"]} />
      </ScrollView>

      {/* Address Picker Modal */}
      {showAddressModal && (
        <Modal animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAddressModal(false)}>
          <Header
            title={ar.selectAddress}
            withoutInsets={Platform.OS === "ios"}
            canGoBack={false}
            right={
              <Pressable onPress={() => setShowAddressModal(false)} className="h-full px-2 justify-center">
                <MCIcon name="close" size={25} />
              </Pressable>
            }
          />
          <View className="flex-1">
            {addresses.length === 0 ? (
              <View className="flex-1 items-center justify-center p-4">
                <MCIcon name="map-marker-off" size={64} color="#d1d5db" />
                <Text className="text-gray-500 text-center mt-4">{ar.noAddressesForCustomer}</Text>
              </View>
            ) : (
              <ScrollView contentContainerStyle={sharedStyles.contentContainer}>
                {addresses.map((addr) => (
                  <Pressable
                    key={addr.id}
                    className="py-4 px-4 border-b border-gray-200"
                    onPress={() => {
                      setValue("addressId", addr.id)
                      setShowAddressModal(false)
                    }}
                  >
                    {addr.label && <Text className="font-semibold text-gray-900 mb-1">{addr.label}</Text>}
                    <View className="flex-row gap-2">
                      {addr.building && (
                        <Text className="text-gray-600 text-sm">
                          {ar.building}: {addr.building}
                        </Text>
                      )}
                      {addr.floor && (
                        <Text className="text-gray-600 text-sm">
                          • {ar.floor}: {addr.floor}
                        </Text>
                      )}
                      {addr.flat && (
                        <Text className="text-gray-600 text-sm">
                          • {ar.flat}: {addr.flat}
                        </Text>
                      )}
                    </View>
                    {addr.instructions && <Text className="text-gray-500 text-sm mt-1">{addr.instructions}</Text>}
                  </Pressable>
                ))}
              </ScrollView>
            )}
          </View>
        </Modal>
      )}
    </>
  )
}

// OrderItems component
type OrderItemsProps = {
  items: { productId: number; quantity: number }[]
  updateQuantity: (productId: number, quantity: number) => void
}

function OrderItems({ items, updateQuantity }: OrderItemsProps) {
  const products = trpc.products.getMultiple.useQuery({ ids: items.map((i) => i.productId) })

  if (items.length === 0) return null
  if (products.isLoading) return <ActivityIndicator className="mt-4" />

  return (
    <View className="bg-white p-4 rounded-lg border border-gray-200">
      <Text className="font-bold text-gray-800 mb-3">{ar.orderItems}</Text>
      {items.map((item) => {
        const product = products.data?.products.find((p) => p.id === item.productId)
        const productImage = product?.images?.at(-1)
        return (
          <View key={item.productId} className="flex-row items-center mb-2">
            <Image
              source={productImage ? { uri: `${ENV.R2_URL}/${productImage.key}` } : undefined}
              className={`w-16 h-16 m-1 ${productImage ? "" : "bg-gray-200"}`}
              contentFit="contain"
              transition={250}
              loading="lazy"
              cachePolicy="memory"
            />

            <View className="flex-1">
              <Text className="text-gray-800 font-semibold" numberOfLines={1}>
                {product?.name}
              </Text>
              <Text className="text-gray-500 text-xs">ID: {item.productId}</Text>
              {product?.upc && <Text className="text-gray-500 text-xs">{product.upc}</Text>}
              {product && (
                <View className="flex-row items-center gap-2 mt-1">
                  <Text className="text-gray-600 text-sm">
                    {priceFromDBFormat(product.price)} {ar.egp}
                  </Text>
                  <Text className="text-gray-400 text-xs">×</Text>
                  <Text className="text-gray-600 text-sm font-bold">{item.quantity}</Text>
                  <Text className="text-gray-400 text-xs">=</Text>
                  <Text className="text-blue-600 text-sm font-bold">
                    {priceFromDBFormat(product.price * item.quantity)} {ar.egp}
                  </Text>
                </View>
              )}
            </View>

            <View className="flex-row items-center gap-2">
              <Pressable onPress={() => updateQuantity(item.productId, item.quantity - 1)} className="w-8 h-8 bg-gray-100 rounded items-center justify-center">
                <MCIcon name="minus" size={16} color="#374151" />
              </Pressable>
              <Text className="w-12 text-center font-semibold">{item.quantity}</Text>
              <Pressable onPress={() => updateQuantity(item.productId, item.quantity + 1)} className="w-8 h-8 bg-gray-100 rounded items-center justify-center">
                <MCIcon name="plus" size={16} color="#374151" />
              </Pressable>
            </View>
          </View>
        )
      })}
    </View>
  )
}

type OrderSummaryProps = {
  items: { productId: number; quantity: number }[]
  addressId?: number
  discount?: number
  customerId?: number
}

function OrderSummary({ items, addressId, discount = 0, customerId }: OrderSummaryProps) {
  const customer = trpc.customers.get.useQuery({ id: customerId as number }, { enabled: customerId !== undefined })
  const deliveryAreas = trpc.deliveryAreas.list.useQuery()
  // const products = trpc.useQueries((t) => items.map((item) => t.products.get({ id: item.productId })))
  const _products = trpc.products.getMultiple.useQuery({ ids: items.map((i) => i.productId) })
  const products = _products.data?.products ?? []

  const subtotal = items.reduce((sum, item) => {
    const product = products.find((p) => p.id === item.productId)
    if (!product) return sum
    return sum + product.price * item.quantity
  }, 0)

  const selectedAddress = customer.data?.addresses?.find((a) => a.id === addressId)
  const deliveryArea = deliveryAreas.data?.find((area) => area.id === selectedAddress?.areaId)
  const deliveryFee = deliveryArea?.fees ?? 0
  const total = subtotal + deliveryFee - discount

  if (items.length === 0) return null

  return (
    <View className="bg-blue-50 p-4 rounded-lg border border-blue-200">
      <Text className="font-bold text-gray-800 mb-3">{ar.orderSummary}</Text>
      <View className="gap-2">
        <View className="flex-row justify-between">
          <Text className="text-gray-600">{ar.subtotal}</Text>
          <Text className="text-gray-800">
            {priceFromDBFormat(subtotal)} {ar.egp}
          </Text>
        </View>
        <View className="flex-row justify-between">
          <Text className="text-gray-600">{ar.deliveryFee}</Text>
          <Text className="text-gray-800">
            {priceFromDBFormat(deliveryFee)} {ar.egp}
          </Text>
        </View>
        {discount > 0 && (
          <View className="flex-row justify-between">
            <Text className="text-gray-600">{ar.discount}</Text>
            <Text className="text-red-600">
              -{priceFromDBFormat(discount)} {ar.egp}
            </Text>
          </View>
        )}
        <View className="flex-row justify-between pt-2 mt-2 border-t border-blue-300">
          <Text className="font-bold text-gray-800">{ar.total}</Text>
          <Text className="font-bold text-lg text-blue-600">
            {priceFromDBFormat(total)} {ar.egp}
          </Text>
        </View>
      </View>
    </View>
  )
}
